/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import { Row, Col } from 'antd';
import {
  EditOutlined,
} from '@ant-design/icons';

import Permissions from '../auth/Permissions';

import BorderlessButton from '../common/buttons/BorderlessButton';
import OntraccrTag from '../common/OntraccrTag';
import TagRender from '../common/TagRender';

import sortByString from '../helpers/helpers';

import {
  getPrice,
  getPriceFloat,
  changeIsNumber as changeChecker,
  hasMarkup,
  getMarkupString,
  getMaterialIcon,
  applyCostChange,
  MATERIAL_COST_MAX_DECIMALS,
  getValidMarkup,
} from './materialsHelpers';

import MaterialsShareButton from './MaterialsShareButton';
import TableFilterDropdown from '../common/tables/TableFilterDropdown';

import { formatEquipmentLabelFromCompanySettings } from '../equipment/equipment.helpers';

export const priceRender = (cost, markup, discount = 0) => (
  hasMarkup(markup) && cost ? `$ ${(cost * (1 + markup) * (1 - discount)).toFixed(2)}` : '$ 0.00'
);

export default ({
  costcodeIdMap = {},
  divisionMap = {},
  equipmentMap = {},
  globalMaterialLocationsMap = {},
  onFolderEdit,
  selectedColumns,
  uniqueLocations = {},
  onShare,
  settings,
  useRedesign = true,
  vendorMap = {},
  isProject = false,
}) => {
  const hasWritePerms = Permissions.hasWrite('MATERIALS');
  let cols = [];

  if (!selectedColumns || selectedColumns.has('code')) {
    cols.push({
      title: 'ID',
      dataIndex: 'code',
      fixed: 'left',
      width: 75,
      sorter: sortByString('code'),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (isProject) {
    cols.push({
      title: 'Name',
      dataIndex: 'name',
      fixed: 'left',
      width: 150,
      ellipsis: true,
      sorter: sortByString('name'),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  } else {
    cols = cols.concat([
      {
        title: <div style={{ paddingLeft: 20 }}>Name</div>,
        dataIndex: 'name',
        fixed: 'left',
        width: 150,
        sorter: sortByString('name'),
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
        render: (_, record) => (
          <Row
            gutter={10}
            justify="start"
            style={{ paddingLeft: hasWritePerms ? 0 : 10 }}
          >
            <Col>
              {getMaterialIcon(record)}
            </Col>
            <Col>
              {record.name}
            </Col>
            {
              (record.isFolder && hasWritePerms)
                ? (
                  <Col>
                    <BorderlessButton
                      style={{ height: 22, margin: 0, padding: 0 }}
                      iconNode={<EditOutlined style={{ margin: '0px 5px' }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onFolderEdit(record);
                      }}
                    />
                  </Col>
                ) : null
            }
          </Row>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 100,
        align: 'center',
        fixed: 'left',
        filters: [{ text: 'Active', value: '1' }, { text: 'Archived', value: '0' }],
        onFilter: (value, record) => parseInt(value, 10) === record.active,
        filterDropdown: TableFilterDropdown,
        defaultFilteredValue: ['1'],
        render: (_, record) => {
          if (record.isFolder) return null;
          return (
            record.active
              ? <OntraccrTag size="small" color="darkgreen">Active</OntraccrTag>
              : <OntraccrTag size="small">Archived</OntraccrTag>
          );
        },
      },
      {
        title: 'Divisions',
        dataIndex: 'divisionIds',
        width: 150,
        render: (divisionIds) => (
          <TagRender
            tagMap={divisionMap}
            tagIds={divisionIds}
            useRedesign={useRedesign}
          />
        ),
      },
    ]);
  }

  if (!selectedColumns || selectedColumns.has('description')) {
    cols.push({
      title: 'Description',
      dataIndex: 'description',
      width: 150,
    });
  }

  if (!selectedColumns || selectedColumns.has('partNumber')) {
    cols.push({
      title: 'Part Number',
      dataIndex: 'partNumber',
      width: 150,
      sorter: sortByString('partNumber'),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!selectedColumns || selectedColumns.has('supplier')) {
    cols.push({
      title: 'Supplier',
      dataIndex: 'supplier',
      width: 150,
      sorter: sortByString('supplier'),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!selectedColumns || selectedColumns.has('vendor')) {
    cols.push({
      title: 'Vendor',
      dataIndex: 'vendorId',
      width: 150,
      sorter: (a, b) => {
        const aVal = (vendorMap[a.vendorId]?.name || '')?.toLowerCase()?.trim();
        const bVal = (vendorMap[b.vendorId]?.name || '')?.toLowerCase()?.trim();
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
        return 0;
      },
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      render: (vendorId) => (vendorMap[vendorId]?.name || ''),
    });
  }

  if (!selectedColumns || selectedColumns.has('cost')) {
    cols.push({
      title: 'Material Cost',
      dataIndex: 'cost',
      width: 150,
      render: (cost) => (cost ? `$ ${cost}` : ''),
      sorter: (a, b) => a.cost - b.cost,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!selectedColumns || selectedColumns.has('markup')) {
    cols.push({
      title: 'Material Markup',
      dataIndex: 'markup',
      width: 150,
      render: (markup, record) => (record.cost ? getMarkupString(markup) : ''),
      sorter: (a, b) => a.markup - b.markup,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!selectedColumns || selectedColumns.has('price')) {
    cols.push({
      title: 'Material Price',
      dataIndex: 'price',
      width: 150,
      render: (_, record) => {
        const {
          cost,
          markup,
        } = record;

        return priceRender(cost, markup);
      },
      sorter: (a, b) => {
        const aPrice = hasMarkup(a.markup) && a.cost ? getPriceFloat(a.cost, a.markup) : a.cost;
        const bPrice = hasMarkup(b.markup) && b.cost ? getPriceFloat(b.cost, b.markup) : b.cost;
        return aPrice - bPrice;
      },
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!selectedColumns || selectedColumns.has('labourCost')) {
    cols.push({
      title: 'Labour Cost',
      dataIndex: 'labourCost',
      width: 150,
      render: (labourCost) => (labourCost ? `$ ${labourCost}` : ''),
      sorter: (a, b) => (
        (a.labourCost ?? Number.MIN_SAFE_INTEGER)
         - (b.labourCost ?? Number.MIN_SAFE_INTEGER)
      ),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!selectedColumns || selectedColumns.has('labourMarkup')) {
    cols.push({
      title: 'Labour Markup',
      dataIndex: 'labourMarkup',
      width: 150,
      render: (markup, record) => (record.labourCost ? getMarkupString(markup) : ''),
      sorter: (a, b) => a.labourMarkup - b.labourMarkup,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!selectedColumns || selectedColumns.has('labourPrice')) {
    cols.push({
      title: 'Labour Price',
      dataIndex: 'labourPrice',
      width: 150,
      render: (_, record) => {
        const {
          labourCost,
          labourMarkup,
        } = record;

        return priceRender(labourCost, labourMarkup);
      },
      sorter: (a, b) => {
        const aPrice = hasMarkup(a.labourMarkup) && a.labourCost
          ? getPriceFloat(a.labourCost, a.labourMarkup)
          : a.labourCost;
        const bPrice = hasMarkup(b.labourMarkup) && b.labourCost
          ? getPriceFloat(b.labourCost, b.labourMarkup)
          : b.labourCost;
        return aPrice - bPrice;
      },
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!isProject) {
    if (!selectedColumns || selectedColumns.has('quantity')) {
      cols.push({
        title: 'Quantity',
        dataIndex: 'quantity',
        width: 150,
        sorter: (a, b) => a.quantity - b.quantity,
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
      });
    }

    if (!selectedColumns || selectedColumns.has('quantityAllocated')) {
      cols.push({
        title: 'Quantity Allocated',
        dataIndex: 'quantityAllocated',
        width: 150,
        sorter: (a, b) => a.quantityAllocated - b.quantityAllocated,
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
      });
    }

    if (!selectedColumns || selectedColumns.has('quantityAvailable')) {
      cols.push({
        title: 'Quantity Available',
        width: 150,
        sorter: ({
          quantity: quantityA = 0,
          quantityAllocated: quantityAllocA = 0,
        }, {
          quantity: quantityB = 0,
          quantityAllocated: quantityAllocB = 0,
        }) => (quantityA - quantityAllocA) - (quantityB - quantityAllocB),
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
        render: (value, { quantity = 0, quantityAllocated = 0 }) => quantity - quantityAllocated,
      });
    }

    if (!selectedColumns || selectedColumns.has('quantityThreshold')) {
      cols.push({
        title: 'Quantity Threshold',
        dataIndex: 'quantityThreshold',
        width: 150,
        render: (quantityThreshold, record) => (record.isFolder ? '' : quantityThreshold),
        filters: [{ text: 'Above Threshold', value: 'above' }, { text: 'Under/Equal Threshold', value: 'under' }],
        onFilter: (value, record) => {
          if (record.isFolder) return false;
          if (value === 'above') {
            return record.quantity > record.quantityThreshold;
          }
          return record.quantity <= record.quantityThreshold;
        },
        filterDropdown: TableFilterDropdown,
      });
    }
  }

  if (!selectedColumns || selectedColumns.has('units')) {
    cols.push({
      title: 'Unit Type',
      dataIndex: 'units',
      width: 150,
      sorter: sortByString('units'),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  if (!selectedColumns || selectedColumns.has('costcode')) {
    cols.push({
      title: 'Cost Code',
      dataIndex: 'costcode',
      width: 150,
      render: (_, record) => {
        const {
          [record.costcodeId]: {
            name: costcodeName,
          } = {},
        } = costcodeIdMap;
        return costcodeName;
      },
    });
  }

  if (!isProject) {
    if (!selectedColumns || selectedColumns.has('locations')) {
      cols.push({
        title: 'Locations',
        dataIndex: 'locations',
        align: 'center',
        width: 200,
        filters: Object.values(uniqueLocations),
        onFilter: (value, { locations = [] }) => (
          locations.some((record) => (
            value === record.globalMaterialLocationsId || value === record.equipmentId
          ))
        ),
        filterDropdown: TableFilterDropdown,
        render: (locations = [], record = {}) => (
          record.isFolder
            ? null
            : locations.map(({ id: locationId, globalMaterialLocationsId, equipmentId }) => {
              const {
                [equipmentId]: {
                  name,
                  code,
                } = {},
              } = equipmentMap;
              const equipmentLabel = formatEquipmentLabelFromCompanySettings({
                name,
                code,
                settings,
              });
              const {
                [globalMaterialLocationsId]: {
                  locationText: globalMaterialLocationsText,
                } = {},
              } = globalMaterialLocationsMap;
              return (
                <OntraccrTag
                  size="small"
                  key={locationId}
                  style={{ margin: 2 }}
                >
                  { equipmentLabel ?? globalMaterialLocationsText }
                </OntraccrTag>
              );
            })
        ),
      });
    }

    if (hasWritePerms) {
      cols.push({
        title: 'Permissions',
        key: 'permissions',
        width: 150,
        align: 'center',
        render: (_, record) => (
          record.isFolder
            ? <MaterialsShareButton onShare={onShare} record={record} />
            : null
        ),
      });
    }
  }

  return cols;
};

/**
 *
 * @param {string} type - ['Cost', 'Markup']
 * @param {number} change - change value
 * @param {string} costType - ['material' {default}, 'labour']
 * @returns {Array} - columns for preview table
 */
export const getPreviewColumns = ({ type, change, costType = 'material', changeMode }) => {
  const changeIsNumber = changeChecker(change);
  const newClass = `material-bulk-change-preview-new-${change > 0 ? 'positive' : 'negative'}-value`;
  const costKey = costType === 'material' ? 'cost' : `${costType}Cost`;
  const markupKey = costType === 'material' ? 'markup' : `${costType}Markup`;
  return (
    [
      {
        title: <div style={{ paddingLeft: 20 }}>Name</div>,
        dataIndex: 'name',
        fixed: 'left',
        width: 100,
        sorter: sortByString('name'),
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
        render: (val) => <div style={{ paddingLeft: 20 }}>{val}</div>,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        width: 100,
      },
      {
        title: 'Part Number',
        dataIndex: 'partNumber',
        width: 100,
        sorter: sortByString('partNumber'),
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Cost',
        dataIndex: costKey,
        width: 100,
        render: (value, record) => {
          const { isFolder } = record;
          if (isFolder) return '';

          const cost = value || 0;

          if (type !== 'Cost' || !changeIsNumber || !change) return `$${cost}`;
          const decimals = costType === 'material' ? MATERIAL_COST_MAX_DECIMALS : 2;
          const newCost = applyCostChange({ cost, change, changeMode }).toFixed(decimals);
          return (
            <>
              <span className="material-bulk-change-preview-old-value">{`$${cost}`}</span>
              <br />
              <span className={newClass}>{`$${newCost}`}</span>
            </>
          );
        },
        sorter: (a, b) => a[costKey] - b[costKey],
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Markup',
        dataIndex: markupKey,
        width: 100,
        render: (markup, record) => {
          const { isFolder } = record;
          if (isFolder) return '';

          const markupPercent = (markup || 0) * 100;
          if (type !== 'Markup' || !changeIsNumber || !change) return `${markupPercent}%`;
          const newMarkup = getValidMarkup({ markup: markupPercent, change });
          return (
            <>
              <span className="material-bulk-change-preview-old-value">{`${markupPercent}%`}</span>
              <br />
              <span className={newClass}>{`${newMarkup}%`}</span>
            </>
          );
        },
        sorter: (a, b) => a[markupKey] - b[markupKey],
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Price',
        dataIndex: 'price',
        width: 100,
        render: (_, record) => {
          const { [markupKey]: markup, [costKey]: rawCost, isFolder } = record;
          if (isFolder) return '';
          const cost = rawCost || 0;
          if (!hasMarkup(markup)) return cost;
          const oldPrice = getPrice(cost, markup * 100);
          if (!changeIsNumber || !change) return oldPrice;
          const newCost = type === 'Cost'
            ? applyCostChange({ cost, change, changeMode })
            : cost;
          const newMarkup = type === 'Markup' ? getValidMarkup({ markup: markup * 100, change }) : markup * 100;
          const newPrice = getPrice(newCost, newMarkup);
          return (
            <>
              <span className="material-bulk-change-preview-old-value">{oldPrice}</span>
              <br />
              <span className={newClass}>{newPrice}</span>
            </>
          );
        },
        sorter: (a, b) => {
          const aPrice = hasMarkup(a[markupKey]) && a[costKey]
            ? getPriceFloat(a[costKey], a[markupKey])
            : a[costKey];
          const bPrice = hasMarkup(b[markupKey]) && b[costKey]
            ? getPriceFloat(b[costKey], b[markupKey])
            : b[costKey];
          return aPrice - bPrice;
        },
        showSorterTooltip: false,
        sortDirections: ['descend', 'ascend'],
      },
    ]
  );
};
